import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Rss, Bot, Image as ImageIcon, Send } from "lucide-react";

import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { CTABanner } from "@/components/marketing/CTABanner";

import {
  buildOrganizationJsonLd,
  buildBreadcrumbJsonLd,
  buildHowToJsonLd,
} from "@/lib/seo";
import { cn } from "@/lib/utils";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

const STEPS = [
  { id: "sources", icon: Rss, screenshot: "/marketing/screenshots/step-1-sources.svg" },
  { id: "generate", icon: Bot, screenshot: "/marketing/screenshots/step-2-generate.svg" },
  { id: "image", icon: ImageIcon, screenshot: "/marketing/screenshots/step-3-image.svg" },
  { id: "publish", icon: Send, screenshot: "/marketing/screenshots/step-4-publish.svg" },
] as const;

const TIMELINE = [
  { id: "day1", offset: "0%" },
  { id: "day2", offset: "33%" },
  { id: "day7", offset: "66%" },
  { id: "day30", offset: "100%" },
] as const;

export default function HowItWorksPage() {
  const { t, i18n } = useTranslation();
  const uiLocale = useLocale();
  const lang = dbLocaleSuffix(uiLocale);

  const jsonLd = [
    buildOrganizationJsonLd(),
    buildBreadcrumbJsonLd([
      { name: t("marketing.breadcrumbs.home"), path: "/" },
      { name: t("marketing.howItWorks.breadcrumb"), path: "/comment-ca-marche" },
    ]),
    buildHowToJsonLd({
      name: t("marketing.howItWorks.hero.title"),
      description: t("marketing.howItWorks.seo.description"),
      totalTimeISO: "P7D",
      steps: STEPS.map((s) => ({
        name: t(`marketing.howItWorks.steps.${s.id}.title`),
        text: t(`marketing.howItWorks.steps.${s.id}.p1`),
        url: `/comment-ca-marche#${s.id}`,
      })),
    }),
  ];

  return (
    <>
      <SeoHead
        title={t("marketing.howItWorks.seo.title")}
        description={t("marketing.howItWorks.seo.description")}
        path="/comment-ca-marche"
        locale={uiLocale}
        jsonLd={jsonLd}
      />

      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.howItWorks.hero.eyebrow")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">{t("marketing.howItWorks.hero.title")}</DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{t("marketing.howItWorks.hero.lead")}</p>
        </div>
      </Section>

      <Rule />

      {STEPS.map((step, i) => {
        const reverse = i % 2 === 1;
        const Icon = step.icon;
        return (
          <Section key={step.id} padding="md" tone={i % 2 === 0 ? "default" : "paper"}>
            <div
              id={step.id}
              className={cn(
                "grid grid-cols-1 items-center gap-12 scroll-mt-28 lg:grid-cols-2",
                reverse && "lg:[&>*:first-child]:order-2",
              )}
            >
              <div>
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-accent-press" />
                  <Eyebrow>{t("marketing.howItWorks.stepLabel", { n: String(i + 1).padStart(2, "0") })}</Eyebrow>
                </div>
                <DisplayHeading level={2} className="mt-4">{t(`marketing.howItWorks.steps.${step.id}.title`)}</DisplayHeading>
                <p className="mt-3 text-base text-muted-foreground">{t(`marketing.howItWorks.steps.${step.id}.subtitle`)}</p>
                <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
                  <p>{t(`marketing.howItWorks.steps.${step.id}.p1`)}</p>
                  <p>{t(`marketing.howItWorks.steps.${step.id}.p2`)}</p>
                  <p>{t(`marketing.howItWorks.steps.${step.id}.p3`)}</p>
                  <p>{t(`marketing.howItWorks.steps.${step.id}.p4`)}</p>
                </div>
              </div>
              <div className="border border-rule bg-background">
                <img
                  src={step.screenshot}
                  alt=""
                  width={1024}
                  height={640}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </Section>
        );
      })}

      <Rule />

      <Section padding="md" dateline={t("marketing.howItWorks.timeline.dateline")}>
        <div className="max-w-2xl">
          <Eyebrow>{t("marketing.howItWorks.timeline.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">{t("marketing.howItWorks.timeline.title")}</DisplayHeading>
        </div>
        <div className="mt-12">
          <div className="relative h-1 w-full bg-rule">
            <div className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-accent-press via-accent-press to-rule" />
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-4">
            {TIMELINE.map((m) => (
              <div key={m.id} className="flex flex-col">
                <span className="dateline">{t(`marketing.howItWorks.timeline.points.${m.id}.dateline`)}</span>
                <h3 className="mt-2 text-display text-lg leading-tight">{t(`marketing.howItWorks.timeline.points.${m.id}.title`)}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{t(`marketing.howItWorks.timeline.points.${m.id}.body`)}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-10">
          <Link to="/fonctionnalites" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-press">
            {t("marketing.howItWorks.timeline.seeFeatures")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      <CTABanner
        tone="accent"
        headline={t("marketing.home.finalCta.title")}
        subhead={t("marketing.home.finalCta.subhead")}
        primary={{ label: t("marketing.home.finalCta.primary"), to: "/dpadmin?intent=signup" }}
        secondary={{ label: t("marketing.home.finalCta.secondary"), to: "/contact" }}
      />
    </>
  );
}
